<template>
  <el-dialog
    :visible="visible"
    width="580px"
    title="扫描策略配置"
    :close-on-click-modal="false"
    append-to-body
    @opened="handleOpened"
    @closed="handleClosed"
    @update:visible="$emit('update:visible', $event)"
  >
    <div style="margin-bottom:12px;">
      <p style="margin:0;font-size:13px;color:#909399;">
        <i class="el-icon-info" style="margin-right:4px;"></i>
        配置源码扫描的分类策略与 AI 披露方式。选择后保存到项目配置中，下次扫描自动沿用。
      </p>
    </div>

    <el-form label-width="100px" label-position="top">
      <!-- 策略类型 -->
      <el-form-item label="扫描策略">
        <el-radio-group v-model="strategyType">
          <el-radio-button label="default">默认规则</el-radio-button>
          <el-radio-button label="custom">自定义脚本</el-radio-button>
        </el-radio-group>
        <div style="margin-top:8px;font-size:12px;color:#909399;line-height:1.6;">
          <template v-if="strategyType === 'default'">
            使用内置预制分类规则（文件名后缀 + 注解检测）进行源码扫描。未被规则识别的文件交给 AI 补充分类。
          </template>
          <template v-else>
            使用自定义脚本进行源码分类。脚本未覆盖的类型回退到内置规则，再回退到 AI 补充分类。
          </template>
        </div>
      </el-form-item>

      <!-- 自定义脚本选择 -->
      <template v-if="strategyType === 'custom'">
        <el-form-item label="自定义脚本">
          <div style="display:flex;gap:8px;">
            <el-select
              v-model="selectedScriptId"
              placeholder="请选择脚本"
              style="flex:1;"
              clearable
              filterable
            >
              <el-option
                v-for="s in scriptList"
                :key="s.id"
                :label="s.name"
                :value="s.id"
              />
            </el-select>
            <el-button type="primary" icon="el-icon-plus" @click="openScriptGenerator">
              新建
            </el-button>
          </div>
          <div v-if="selectedScript" style="margin-top:10px;padding:12px;background:#F5F7FA;border-radius:6px;">
            <div style="display:flex;justify-content:space-between;align-items:center;">
              <span style="font-weight:500;font-size:13px;color:#303133;">
                <i class="el-icon-document" style="margin-right:4px;color:#409EFF;"></i>
                {{ selectedScript.name }}
              </span>
              <el-button type="text" size="mini" @click="editScript(selectedScript)">
                <i class="el-icon-edit-outline"></i> 编辑
              </el-button>
            </div>
            <p style="margin:6px 0 0;font-size:12px;color:#909399;white-space:pre-wrap;">
              {{ selectedScript.description || '无描述' }}
            </p>
            <div style="margin-top:6px;font-size:11px;color:#C0C4CC;">
              内容长度: {{ (selectedScript.content || '').length }} 字符
            </div>
          </div>
          <div v-if="scriptList.length === 0" style="margin-top:8px;color:#909399;font-size:12px;">
            暂无自定义脚本，点击「新建」让 AI 生成
          </div>
        </el-form-item>
      </template>

      <!-- AI 披露策略 -->
      <el-form-item label="AI 补充分析策略">
        <div style="display:flex;flex-direction:column;gap:8px;">
          <el-radio v-model="aiDisclosure" label="COMPACT">
            <div style="display:inline-flex;flex-direction:column;">
              <span>摘要模式 <el-tag size="mini" type="warning">最少 Token</el-tag></span>
              <span style="font-size:12px;color:#909399;font-weight:400;">只传文件摘要（包名、注解、继承关系），AI 根据摘要分类，速度快 Token 消耗最小</span>
            </div>
          </el-radio>
          <el-radio v-model="aiDisclosure" label="FULL">
            <div style="display:inline-flex;flex-direction:column;">
              <span>全文模式 <el-tag size="mini">完整</el-tag></span>
              <span style="font-size:12px;color:#909399;font-weight:400;">传摘要 + 文件前 100 行内容，AI 基于完整信息分类，最准确但 Token 消耗最大</span>
            </div>
          </el-radio>
          <el-radio v-model="aiDisclosure" label="PROGRESSIVE">
            <div style="display:inline-flex;flex-direction:column;">
              <span>渐进式披露 <el-tag size="mini" type="success">平衡</el-tag></span>
              <span style="font-size:12px;color:#909399;font-weight:400;">先传摘要尝试分类，未识别的文件再追加内容重新分析，兼顾效率与准确率</span>
            </div>
          </el-radio>
        </div>
      </el-form-item>
    </el-form>

    <template #footer>
      <div style="display:flex;justify-content:space-between;align-items:center;">
        <el-button type="info" plain @click="handleSkip">
          <i class="el-icon-d-arrow-left"></i> 跳过扫描
        </el-button>
        <div style="display:flex;gap:8px;">
          <el-button @click="$emit('update:visible', false)">取消</el-button>
          <el-button type="primary" @click="handleConfirm" :loading="saving">
            {{ saving ? '保存中...' : '确认并扫描' }}
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { getScanScriptList, updateProject } from "@/api/dataApi";

export default {
  name: 'ScanStrategyDialog',
  props: {
    visible: Boolean,
    projectId: { type: Number, default: null },
    projectName: { type: String, default: '' },
    projectCode: { type: String, default: '' },
    /** { strategyType, aiDisclosure, scanScriptId } */
    initialConfig: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      strategyType: 'default',
      aiDisclosure: 'COMPACT',
      selectedScriptId: null,
      scriptList: [],
      saving: false,
      loadingScripts: false
    };
  },
  computed: {
    selectedScript() {
      if (!this.selectedScriptId) return null;
      return this.scriptList.find(s => s.id === this.selectedScriptId) || null;
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.strategyType = this.initialConfig.strategyType || 'default';
        this.aiDisclosure = this.initialConfig.aiDisclosure || 'COMPACT';
        this.selectedScriptId = this.initialConfig.scanScriptId || null;
        if (this.initialConfig.scanScriptId) {
          this.strategyType = 'custom';
        }
      }
    }
  },
  methods: {
    async handleOpened() {
      await this.loadScriptList();
    },
    handleClosed() {
      // reset
    },
    async loadScriptList() {
      this.loadingScripts = true;
      try {
        const res = await getScanScriptList();
        this.scriptList = res.data || [];
      } catch (e) {
        console.error('加载脚本列表失败:', e);
        this.scriptList = [];
      } finally {
        this.loadingScripts = false;
      }
    },
    openScriptGenerator() {
      this.$emit('openScriptGenerator');
    },
    editScript(script) {
      this.$emit('openScriptGenerator', script);
    },
    async handleConfirm() {
      this.saving = true;
      try {
        // Save strategy to project
        if (this.projectId) {
          const payload = {
            id: this.projectId,
            name: this.projectName,
            code: this.projectCode,
            aiDisclosure: this.aiDisclosure
          };
          if (this.strategyType === 'custom') {
            payload.scanScriptId = this.selectedScriptId || null;
          } else {
            payload.scanScriptId = null;
          }
          await updateProject(payload);
        }
        this.$emit('confirm', {
          strategyType: this.strategyType,
          aiDisclosure: this.aiDisclosure,
          scanScriptId: this.strategyType === 'custom' ? (this.selectedScriptId || null) : null
        });
        this.$emit('update:visible', false);
      } catch (e) {
        console.error('保存扫描策略失败:', e);
        this.$message.error('保存策略失败: ' + (e.message || ''));
      } finally {
        this.saving = false;
      }
    },
    handleSkip() {
      this.$emit('skip');
      this.$emit('update:visible', false);
    }
  }
};
</script>
